/**
 * Custom Form Validation Module
 * Bypasses Shopify default validation and provides real-time feedback.
 */

class FormValidator {
  constructor(formElement) {
    this.form = formElement;
    this.inputs = this.form.querySelectorAll('input, textarea, select');
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.isValidating = false;

    this.init();
  }

  init() {
    this.inputs.forEach(input => {
      let debounceTimer;

      // Validate on blur
      input.addEventListener('blur', () => {
        clearTimeout(debounceTimer);
        this.validateField(input);
      });

      // Validate on input with debounce
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          this.validateField(input);
        }, 500);
      });
    });

    this.form.addEventListener('submit', (e) => {
      this.handleSubmit(e);
    });
  }

  /**
   * Main validation logic for a single field
   */
  validateField(field) {
    const label = field.getAttribute('data-label') || 'Field';
    let errorMessage = '';
    let isValid = true;

    const value = field.value.trim();

    // 1. Required Rule
    if (field.classList.contains('required') && !value) {
      errorMessage = `${label} is required`;
      isValid = false;
    }

    // 2. Email Rule
    if (isValid && field.type === 'email' && value) {
      const emailRegex = /^(?!\.)(?!.*?\.\.)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+(?<!\.)@(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)+[a-zA-Z]{2,}$/;
      const localPart = value.split('@')[0];

      if (value.length > 254) {
        errorMessage = `Email address is too long`;
        isValid = false;
      } else if (localPart && localPart.length > 64) {
        errorMessage = `Email local part is too long`;
        isValid = false;
      } else if (!emailRegex.test(value)) {
        errorMessage = `Please enter a valid email address`;
        isValid = false;
      }
    }

    // 3. Phone Rule (10-15 digits, no repeated/spam)
    if (isValid && field.type === 'tel' && value) {
      const phoneRegex = /^\+?[0-9]+$/;
      if (!phoneRegex.test(value)) {
        errorMessage = `Phone number can only contain digits and cannot have spaces or symbols`;
        isValid = false;
      } else {
        const phoneDigits = value.replace(/\D/g, '');
        if (phoneDigits.length < 10 || phoneDigits.length > 15) {
          errorMessage = `Please enter a valid phone number`;
          isValid = false;
        } else if (/^(\d)\1+$/.test(phoneDigits)) {
          errorMessage = `Phone number cannot contain all identical or repeated digits`;
          isValid = false;
        }
      }
    }

    // 4. Password Rules
    if (isValid && field.type === 'password' && value) {
      if (value.length < 8 || value.length > 40) {
        errorMessage = `Password must be between 8 and 40 characters`;
        isValid = false;
      } else if (!/[A-Z]/.test(value)) {
        errorMessage = `Password must contain at least one uppercase letter`;
        isValid = false;
      } else if (!/[a-z]/.test(value)) {
        errorMessage = `Password must contain at least one lowercase letter`;
        isValid = false;
      } else if (!/[0-9]/.test(value)) {
        errorMessage = `Password must contain at least one number`;
        isValid = false;
      } else if (!/[@$!%*?&^#()_\-+=]/.test(value)) {
        errorMessage = `Password must contain at least one special character`;
        isValid = false;
      }

      // Confirm Password
      if (isValid && field.classList.contains('confirm-password')) {
        const originalPassword = this.form.querySelector('input[type="password"]:not(.confirm-password)');
        if (originalPassword && value !== originalPassword.value) {
          errorMessage = `Passwords do not match`;
          isValid = false;
        }
      }
    }

    // 5. Min Characters Rule (min-X-characters)
    if (isValid && value) {
      const minMatch = Array.from(field.classList).find(cls => cls.startsWith('min-') && cls.endsWith('-characters'));
      if (minMatch) {
        const min = parseInt(minMatch.split('-')[1]);
        if (value.length < min) {
          errorMessage = `Please enter at least ${min} characters`;
          isValid = false;
        }
      }
    }

    // 6. Max Characters Rule (max-X-characters)
    if (isValid && value) {
      const maxMatch = Array.from(field.classList).find(cls => cls.startsWith('max-') && cls.endsWith('-characters'));
      if (maxMatch) {
        const max = parseInt(maxMatch.split('-')[1]);
        if (value.length > max) {
          errorMessage = `Maximum of ${max} characters allowed`;
          isValid = false;
        }
      }
    }

    // 7. No Numbers Rule (no-numbers)
    if (isValid && field.classList.contains('no-numbers') && value) {
      const numberRegex = /\d/;
      if (numberRegex.test(value)) {
        errorMessage = `${label} cannot contain numbers`;
        isValid = false;
      }
    }

    // 8. No Special Characters Rule (no-special-chars)
    if (isValid && field.classList.contains('no-special-chars') && value) {
      const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/;
      if (specialCharRegex.test(value)) {
        errorMessage = `${label} cannot contain symbols or special characters`;
        isValid = false;
      }
    }

    // 9. No Multiple Spaces Rule (no-multiple-spaces)
    if (isValid && field.classList.contains('no-multiple-spaces') && value) {
      if (/\s{2,}/.test(field.value)) {
        errorMessage = `Continuous spaces are not allowed`;
        isValid = false;
      }
    }

    // 10. Website Rule
    if (isValid && field.classList.contains('website-validation') && value) {
      const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/\S*)?$/;
      if (!urlRegex.test(value)) {
        errorMessage = `Please enter a valid website URL`;
        isValid = false;
      } else if (value.length > 255) {
        errorMessage = `Website URL must not exceed 255 characters`;
        isValid = false;
      }
    }

    // 11. Message Restrictions (HTML, special symbols)
    if (isValid && (field.id === 'ContactForm-body' || field.id === 'CommentBody') && value) {
      const htmlTagRegex = /<\/?[^>]+(>|$)/;
      const restrictedSymbolRegex = /[{}\[\]<>|^]/;
      if (htmlTagRegex.test(value) || restrictedSymbolRegex.test(value)) {
        errorMessage = `Message contains invalid or restricted characters`;
        isValid = false;
      }
    }

    // Skip hidden or non-interactive fields from showing errors
    if (field.type === 'hidden' || field.style.display === 'none') {
      return true;
    }

    if (isValid) {
      this.removeError(field);
    } else {
      this.showError(field, errorMessage);
    }

    return isValid;
  }

  showError(field, message) {
    const errorId = `${field.id}-error`;
    let errorElement = field.parentNode.querySelector(`.field-error#${errorId}`);

    if (errorElement) {
      if (errorElement.textContent === message) return;
      errorElement.textContent = message;
    } else {
      field.classList.add('has-error');
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', errorId);

      errorElement = document.createElement('small');
      errorElement.id = errorId;
      errorElement.className = 'field-error';
      errorElement.textContent = message;
      field.parentNode.appendChild(errorElement);
    }
  }

  removeError(field) {
    if (!field.classList.contains('has-error')) return;

    field.classList.remove('has-error');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');

    const errorId = `${field.id}-error`;
    const errorElement = field.parentNode.querySelector(`.field-error#${errorId}`);
    if (errorElement) {
      errorElement.remove();
    }
  }

  handleSubmit(e) {
    if (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    let firstInvalid = null;
    let isFormValid = true;

    this.inputs.forEach(input => {
      // Don't validate hidden fields
      if (input.type === 'hidden') return;

      const isFieldValid = this.validateField(input);
      if (!isFieldValid) {
        isFormValid = false;
        if (!firstInvalid) firstInvalid = input;
      }
    });

    if (!isFormValid) {
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstInvalid.focus(), 500);
      }
      return false;
    } else {
      // Perform one last check
      if (this.isValidating) return false;
      this.isValidating = true;

      if (this.submitButton) {
        this.submitButton.classList.add('is-loading');
        this.submitButton.disabled = true;
      }

      this.form.submit();
      return true;
    }
  }
}

function handleSuccessToast() {
  const successMessages = document.querySelectorAll(
    '.form-notification.success, .footer-newsletter-message.is-success'
  );
  if (!successMessages.length) return;

  successMessages.forEach(successMessage => {
    // initCustomValidation runs on DOMContentLoaded and again after 1s, so
    // flag handled notices rather than stacking a second hide timer on them.
    if (successMessage.dataset.toastHandled) return;
    successMessage.dataset.toastHandled = 'true';

    // 1. Auto-hide after 5 seconds
    setTimeout(() => {
      successMessage.style.transition = 'opacity 0.5s ease';
      successMessage.style.opacity = '0';
      setTimeout(() => successMessage.remove(), 500);
    }, 5000);
  });

  // 2. Clean up URL to prevent toast persistence on refresh
  if (window.history.replaceState) {
    const url = new URL(window.location.href);
    url.searchParams.delete('contact_posted');
    url.searchParams.delete('customer_posted');
    url.hash = ''; // Clear #contact_form
    window.history.replaceState({}, document.title, url.toString());
  }
}

function initCustomValidation() {
  const forms = document.querySelectorAll('form[id="CustomContactForm"], form[id*="CustomContactForm"], form[id="comment_form"], form[id="FooterNewsletterForm"]');
  forms.forEach(form => {
    if (!form.dataset.validatorInitialized) {
      new FormValidator(form);
      form.dataset.validatorInitialized = 'true';
    }
  });
  handleSuccessToast();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomValidation);
} else {
  initCustomValidation();
}
// Also try after a short delay for any late-rendered forms
setTimeout(initCustomValidation, 1000);
